import type { InitAppResponse } from "@hz/common/api-types/init";
import type { EntityType, OurRoles, OurStages } from "@hz/common/constants";
import type { MdySubscription } from "@hz/common/monday/subscription.types";
import type { MdySessionTokenType } from "@hz/common/monday/types";
import { getHasPaidSubscription } from "@hz/common/utils";
import type { PayloadAction } from "@reduxjs/toolkit";
import { createAsyncThunk, createSelector, createSlice } from "@reduxjs/toolkit";
import { setTags, setUser } from "@sentry/react";

import { decodeJwt } from "jose";

import { api } from "@state/apiSlice";

import mondaySdk from "@monday/sdk";

import { signPayload } from "@utils/jwt";
import logger from "@utils/logger";

import type { ContextData } from "./types";

type MondayState = {
  context: ContextData | null;
  isLoading: boolean;
  error: FetchMondayContextError | null;
  token: string | null;
  subscription: MdySubscription | null;
  hasPaidSubscription: boolean;
  isActive: boolean;
  isTrial: boolean;
  boardId: number | null;
  itemId: number | null;
  init: {
    role: OurRoles | null;
    stage: OurStages | null;
    isNewUser: boolean;
    connections: {
      isMondayConnected: boolean;
      isGoogleConnected: boolean;
    };
    mapping: {
      hasMapping: boolean;
      entityType: EntityType | null;
    };
    merchantId?: string | null;
  };
};

const initialState: MondayState = {
  context: null,
  isLoading: false,
  error: null,
  token: null,
  subscription: null,
  isTrial: false,
  hasPaidSubscription: false,
  isActive: false,
  boardId: null,
  itemId: null,
  init: {
    role: null,
    stage: null,
    isNewUser: false,
    connections: {
      isMondayConnected: false,
      isGoogleConnected: false,
    },
    mapping: {
      hasMapping: false,
      entityType: null,
    },
    merchantId: null,
  },
};

export const fetchAndSignMondayData = createAsyncThunk(
  "monday/fetchAndSignMondayData",
  async (_, { getState, rejectWithValue }) => {
    try {
      const state = getState() as { monday: MondayState };
      const context = state.monday.context;
      const subscription = state.monday.subscription;

      // Combine the data into the required format
      const combinedData = {
        boardId: context?.boardId,
        mondayItemId: context?.itemId,
        clientId: context?.app.clientId,
        mondayUserId: context?.user.id,
        mondayAccountId: context?.account.id,
        slug: null,
        appId: context?.app.id,
        appVersionId: context?.appVersion.id,
        isAdmin: context?.user.isAdmin,
        isViewOnly: context?.user.isViewOnly,
        isGuest: context?.user.isGuest,
        userKind: context?.user.isAdmin ? "admin" : "user",
        role: state.monday.init.role,
        subscription,
      };

      const token = await signPayload(combinedData);
      return token;
    } catch {
      return rejectWithValue("Failed to fetch or sign data");
    }
  },
);

type FetchMondayContextPayload = {
  context: ContextData;
  subscription: MdySubscription | null;
  isTrial: boolean;
  hasPaidSubscription: boolean;
  isActive: boolean;
};

type FetchMondayContextError = { message: string; status: number };

export const fetchMondayContext = createAsyncThunk<
  FetchMondayContextPayload,
  void,
  { rejectValue: FetchMondayContextError }
>("monday/fetchMondayContext", async (_, { rejectWithValue }) => {
  try {
    const contextRes = await mondaySdk.get("context");

    // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition
    if (contextRes.data) {
      const context = contextRes.data as ContextData;

      setTags({ mondayAccountId: context.account.id, mondayUserId: context.user.id });
      setUser({ id: context.account.id });

      const sessionTokenRes = await mondaySdk.get("sessionToken");

      if (sessionTokenRes.data) {
        const sessionToken = sessionTokenRes.data;
        const decodedMdySessionToken = decodeJwt(sessionToken) as MdySessionTokenType;
        const subscription = decodedMdySessionToken.dat.subscription || null;

        const isTrial = subscription?.is_trial || false;
        const hasPaidSubscription = subscription ? getHasPaidSubscription(subscription) : false;
        const isActive = isTrial || hasPaidSubscription;

        return {
          context,
          subscription,
          isTrial,
          hasPaidSubscription,
          isActive,
        };
      } else {
        throw new Error("Failed to retrieve monday session token.");
      }
    } else {
      throw new Error("Failed to retrieve monday context data.");
    }
  } catch (error: unknown) {
    if (error instanceof Error) {
      return rejectWithValue({ message: error.message, status: 500 });
    } else if (typeof error === "string") {
      return rejectWithValue({ message: error, status: 500 });
    } else {
      return rejectWithValue({ message: "An unknown error occurred", status: 500 });
    }
  }
});

export const fetchInit = createAsyncThunk<
  InitAppResponse,
  { forceRefetch?: boolean },
  { rejectValue: FetchMondayContextError }
>("monday/fetchInit", async ({ forceRefetch = false }, { getState, dispatch, rejectWithValue }) => {
  try {
    const state = getState() as { monday: MondayState };
    const boardId = state.monday.context?.boardId;

    const itemId = state.monday.context?.itemId;

    if (!boardId) {
      throw new Error("Cannot call fetchInit without boardId.");
    }

    if (!itemId) {
      throw new Error("Cannot call fetchInit without boardId.");
    }

    const resp = await dispatch(api.endpoints.init.initiate({ boardId, itemId }, { forceRefetch })).unwrap();

    return resp;
  } catch (error: unknown) {
    if (error instanceof Error) {
      return rejectWithValue({ message: error.message, status: 500 });
    } else if (typeof error === "string") {
      return rejectWithValue({ message: error, status: 500 });
    } else {
      return rejectWithValue({ message: "An unknown error occurred", status: 500 });
    }
  }
});

const mondaySlice = createSlice({
  name: "monday",
  initialState,
  reducers: {},
  extraReducers: (builder) => {
    builder
      .addCase(fetchMondayContext.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(fetchMondayContext.fulfilled, (state, action: PayloadAction<FetchMondayContextPayload>) => {
        const payload = action.payload;

        state.context = payload.context;
        state.subscription = payload.subscription;
        state.isTrial = payload.isTrial;
        state.hasPaidSubscription = payload.hasPaidSubscription;
        state.boardId = payload.context.boardId;
        state.itemId = payload.context.itemId;

        const DISABLE_SUBSCRIPTIONS = Boolean(import.meta.env.VITE_DISABLE_SUBSCRIPTIONS);

        state.isActive = DISABLE_SUBSCRIPTIONS ? true : payload.isActive;
        state.isLoading = false;
      })
      .addCase(fetchMondayContext.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload as FetchMondayContextError;
      })

      .addCase(fetchAndSignMondayData.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(fetchAndSignMondayData.fulfilled, (state, action: PayloadAction<string>) => {
        state.token = action.payload;
        state.isLoading = false;
      })
      .addCase(fetchAndSignMondayData.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload as FetchMondayContextError;
      })
      .addCase(fetchInit.pending, (state) => {
        logger.debug("[fetchInit]: Pending...");
        state.isLoading = true;
        state.error = null;
      })
      .addCase(fetchInit.fulfilled, (state, action: PayloadAction<InitAppResponse>) => {
        logger.debug("[fetchInit] Fulfilled with payload:", action.payload);
        state.init = action.payload;
        state.isLoading = false;
        logger.debug("[fetchInit] Updated state.role to:", state.init.role);
      })
      .addCase(fetchInit.rejected, (state, action) => {
        logger.error("[fetchInit] Rejected with error:", action.payload);
        state.isLoading = false;
        state.error = action.payload as FetchMondayContextError;
      });
  },
});

export const selectMondayState = (state: { monday: MondayState }) => state.monday;

export const selectMondayContextData = (state: { monday: MondayState }) => state.monday.context;

export const selectMondayIsViewOnly = createSelector(
  [selectMondayContextData],
  (state) => state?.user.isViewOnly ?? true,
);

export const selectMondayIsGuest = createSelector(
  [selectMondayContextData],
  (state) => state?.user.isGuest ?? true,
);

export const selectMondayIsAdmin = createSelector(
  [selectMondayContextData],
  (data) => data?.user.isAdmin ?? false,
);

export const selectMondayAccountId = createSelector(
  [selectMondayContextData],
  (state) => state?.account.id || null,
);

export const selectMondayUserId = createSelector(
  [selectMondayContextData],
  (state) => state?.user.id || null,
);

export const selectMondayItemId = createSelector([selectMondayContextData], (state) => state?.itemId || null);

export const selectMondayBoardId = createSelector(
  [selectMondayContextData],
  (state) => state?.boardId || null,
);

export const selectIsTrial = (state: { monday: MondayState }) => state.monday.isTrial;

export const selectToken = (state: { monday: MondayState }) => state.monday.token;

export const selectInit = (state: { monday: MondayState }) => state.monday.init;

export const selectMondayBoardViewId = createSelector(
  [selectMondayContextData],
  (state) => state?.boardViewId || null,
);

export const selectMondaySubscription = createSelector(
  [(state: { monday: MondayState }) => state.monday],
  (monday) => monday.subscription,
);

export default mondaySlice.reducer;
